import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarDays, Download, RefreshCw } from "lucide-react";
import type { Report } from "../types/models";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { SelectField } from "../components/Field";
import { IconButton } from "../components/IconButton";
import { PageHeader } from "../components/PageHeader";
import { Skeleton } from "../components/Skeleton";
import { useI18n } from "../i18n/I18nProvider";
import { useToast, type ToastOptions } from "../components/Toast";
import { ApiRequestError } from "../services/api";
import { createReport, downloadReport, getReport, listReports } from "../services/reports";
import { formatDateLabel, todayInputValue } from "../lib/dates";

export function ReportsPage() {
  const { t, locale, translateError } = useI18n();
  const { showToast } = useToast();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [format, setFormat] = useState<"csv" | "json" | "pdf" | "xlsx">("csv");
  const [from, setFrom] = useState(todayInputValue());
  const [to, setTo] = useState(todayInputValue());

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setReports(await listReports());
    } catch (error) {
      handleError(error, translateError, showToast);
    } finally {
      setLoading(false);
    }
  }, [showToast, translateError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const requestReport = async () => {
    try {
      const report = await createReport({ kind: "hydration", format });
      if (report) {
        setReports((current) => [report, ...current]);
        void pollReport(report.id, setReports);
      } else {
        await refresh();
      }
    } catch (error) {
      handleError(error, translateError, showToast);
    }
  };

  const download = async (report: Report) => {
    try {
      await downloadReport(report);
      showToast({ title: t("common.done") });
    } catch (error) {
      handleError(error, translateError, showToast);
    }
  };

  return (
    <>
      <PageHeader
        title={t("reports.title")}
        actions={
          <IconButton label={t("common.refresh")} onClick={() => void refresh()}>
            <RefreshCw size={18} aria-hidden="true" />
          </IconButton>
        }
      />
      <div className="stack">
        <section className="panel stack">
          <h2>{t("reports.create")}</h2>
          <div className="report-date-grid">
            <ReportDatePicker label={t("reports.from")} locale={locale} value={from} onChange={setFrom} />
            <ReportDatePicker label={t("reports.to")} locale={locale} value={to} onChange={setTo} />
          </div>
          <p className="field__hint">
            {from} - {to}. {t("reports.backendDefaultRange")}
          </p>
          <SelectField
            label={t("reports.format")}
            value={format}
            onChange={(event) => setFormat(event.target.value as "csv" | "json" | "pdf" | "xlsx")}
            options={[
              { label: "CSV", value: "csv" },
              { label: "JSON", value: "json" },
              { label: "PDF", value: "pdf" },
              { label: "XLSX", value: "xlsx" }
            ]}
          />
          <Button onClick={() => void requestReport()}>{t("reports.create")}</Button>
          <p className="field__hint">{t("reports.emailUnavailable")}</p>
        </section>
        <section className="panel stack">
          <h2>{t("reports.status")}</h2>
          {loading ? (
            <Skeleton rows={4} />
          ) : reports.length ? (
            <div className="list">
              {reports.map((report) => (
                <div className="list-item" key={report.id}>
                  <div className="list-item__main">
                    <strong>{report.format?.toUpperCase() ?? "CSV"}</strong>
                    <span className="list-item__meta">{report.status ?? t("common.unknown")}</span>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Download size={16} aria-hidden="true" />}
                    disabled={report.status !== "completed"}
                    onClick={() => void download(report)}
                  >
                    {t("reports.download")}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title={t("common.empty")} />
          )}
        </section>
      </div>
    </>
  );
}

function ReportDatePicker({ label, locale, value, onChange }: { label: string; locale: string; value: string; onChange: (value: string) => void }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const openNativePicker = () => {
    const input = inputRef.current;
    if (!input) {
      return;
    }
    try {
      input.showPicker();
    } catch {
      input.focus();
    }
  };

  return (
    <label className="report-date-field">
      <span className="field__label">{label}</span>
      <span className="report-date-control">
        <span className="report-date-control__surface" aria-hidden="true">
          <CalendarDays size={16} aria-hidden="true" />
          <span>{formatDateLabel(value, locale)}</span>
        </span>
        <input
          ref={inputRef}
          className="report-date-input"
          aria-label={label}
          type="date"
          value={value}
          onClick={openNativePicker}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              openNativePicker();
            }
          }}
          onChange={(event) => onChange(event.target.value)}
        />
      </span>
    </label>
  );
}

async function pollReport(id: string, setReports: (updater: (current: Report[]) => Report[]) => void): Promise<void> {
  for (let attempt = 0; attempt < 20; attempt += 1) {
    await new Promise((resolve) => window.setTimeout(resolve, attempt < 12 ? 3000 : 6000));
    const report = await getReport(id);
    if (!report) {
      continue;
    }
    setReports((current) => current.map((item) => (item.id === id ? report : item)));
    if (report.status === "completed" || report.status === "failed" || report.status === "expired") {
      return;
    }
  }
}

function handleError(error: unknown, translateError: (code: string) => string, showToast: (toast: ToastOptions) => string): void {
  if (error instanceof ApiRequestError) {
    showToast({ title: translateError(error.code), tone: "error" });
  } else {
    showToast({ title: translateError("INTERNAL_ERROR"), tone: "error" });
  }
}
